// Expected hours a week (22/10/2026, Karan): a company setting with a
// per-person override. ASP's is 37:30 (organisations/asp.settings
// .weeklyContractMinutes = 2250); another company's is blank until it sets one,
// and blank means Track doesn't talk about "expected" or "short" at all.
// A person's own figure (users/{id}.weeklyContractMinutes, e.g. a part-timer)
// wins over the company's. Pure: callers pass the setting in, so tests and the
// phone can use it without the org loader.
import { sheetKey } from './me.js';
import { isTimesheetExempt } from './timesheet-required.js';

const minutes = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

/** The minutes this person is expected to log a week, or null when nobody set one. */
export function contractMinutes(user, orgMinutes) {
  return minutes(user && user.weeklyContractMinutes) ?? minutes(orgMinutes);
}

/** The minutes Track holds this person's OWN week against ("37:30 expected ·
 *  short", the submit check, the lock nudge): null for someone whose timesheets
 *  are not required (users.timesheetExempt, 26/10/2026) — they see hours
 *  logged, never a shortfall. Team's utilisation keeps contractMinutes. */
export function expectedMinutes(user, orgMinutes) {
  return isTimesheetExempt(user) ? null : contractMinutes(user, orgMinutes);
}

/** The signed-in person's own users doc from usersById(): ASP's are keyed by the
 *  email's local part, a phone-only member's by their uid. */
export function userFor(users, session) {
  if (!users || !session) return null;
  const key = sheetKey(session);
  if (key && users[key]) return users[key];
  const email = String(session.email || '').toLowerCase();
  return Object.values(users).find((u) => (email && String(u.email || '').toLowerCase() === email)
    || (session.uid && (u.uid === session.uid || u.id === session.uid))) || null;
}

/** "H:MM" for a contract figure; '' when there is none. */
export const contractLabel = (min) => (min ? `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}` : '');

/** What someone typed for an expected week: "37:30", "37.5", "40" or blank.
 *  { ok, value } — value is minutes, or null for blank (no expected week). */
export function parseContract(text) {
  const t = String(text == null ? '' : text).trim();
  if (!t) return { ok: true, value: null };
  let min = null;
  const hm = t.match(/^(\d{1,2}):([0-5]\d)$/);
  if (hm) min = Number(hm[1]) * 60 + Number(hm[2]);
  else if (/^\d{1,2}(\.\d+)?$/.test(t)) min = Math.round(Number(t) * 60);
  if (min == null) return { ok: false, error: 'Use hours and minutes, e.g. 37:30.' };
  if (min < 60 || min > 80 * 60) return { ok: false, error: 'Between 1:00 and 80:00 a week.' };
  return { ok: true, value: min };
}
